// src/components/DocumentGenerationAnimation.js
import React, { useState, useEffect } from 'react';
import { Modal, Progress, Typography, Space, List, Card } from 'antd';
import { 
    CheckCircleOutlined, 
    FileTextOutlined, 
    LoadingOutlined,
    CheckOutlined 
} from '@ant-design/icons';

const { Title, Text } = Typography;

const DocumentGenerationAnimation = ({ isGenerating, isCompleted, documents = [] }) => {
    const [currentDoc, setCurrentDoc] = useState(0);
    const [showSuccess, setShowSuccess] = useState(false);
    const [progress, setProgress] = useState(0);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        // Проверка мобильного устройства
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (isGenerating && documents.length > 0) {
            const interval = setInterval(() => {
                setCurrentDoc((prev) => {
                    const next = (prev + 1) % documents.length;
                    setProgress((next / documents.length) * 100);
                    return next;
                });
            }, 1500);
            return () => clearInterval(interval);
        }
    }, [isGenerating, documents.length]);

    useEffect(() => {
        if (isCompleted) {
            setProgress(100);
            setTimeout(() => setShowSuccess(true), 300);
        }
    }, [isCompleted]);

    if (!isGenerating && !isCompleted) return null;

    const modalContent = (
        <div style={{ padding: isMobile ? '12px' : '20px 0' }}>
            {isGenerating && !isCompleted && (
                <div style={{ textAlign: 'center' }}>
                    <div style={{ marginBottom: isMobile ? 20 : 30 }}>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                            <LoadingOutlined 
                                style={{ 
                                    fontSize: isMobile ? 60 : 80, 
                                    color: '#1890ff',
                                    animation: 'spin 2s linear infinite'
                                }} 
                            />
                            <FileTextOutlined 
                                style={{ 
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    fontSize: isMobile ? 24 : 30,
                                    color: '#1890ff'
                                }} 
                            />
                        </div>
                    </div>
                    
                    <Title level={isMobile ? 4 : 3} style={{ marginBottom: isMobile ? 16 : 24 }}>
                        Generowanie dokumentów
                    </Title>
                    
                    <Progress 
                        percent={Math.round(progress)} 
                        strokeColor="#1890ff"
                        style={{ marginBottom: isMobile ? 20 : 30 }}
                    />
                    
                    <Card 
                        bodyStyle={{ padding: 0 }} 
                        style={{ 
                            marginBottom: isMobile ? 16 : 20,
                            boxShadow: isMobile ? 'none' : '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                    >
                        <List
                            dataSource={documents}
                            renderItem={(doc, index) => (
                                <List.Item
                                    style={{
                                        padding: isMobile ? '10px 16px' : '12px 24px',
                                        backgroundColor: index === currentDoc ? '#e6f7ff' : 'transparent',
                                        transition: 'all 0.3s ease',
                                        borderLeft: index === currentDoc ? '4px solid #1890ff' : '4px solid transparent'
                                    }}
                                >
                                    <Space 
                                        style={{ 
                                            width: '100%', 
                                            justifyContent: 'space-between',
                                            flexWrap: isMobile ? 'nowrap' : undefined
                                        }}
                                    >
                                        <Text 
                                            strong={index === currentDoc}
                                            style={{ 
                                                fontSize: isMobile ? 14 : 16,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: isMobile ? '70%' : 'auto'
                                            }}
                                        >
                                            {doc}
                                        </Text>
                                        {index === currentDoc && (
                                            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                                <div 
                                                    style={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#1890ff',
                                                        animation: 'bounce 1.4s ease-in-out infinite'
                                                    }}
                                                />
                                                <div 
                                                    style={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#1890ff',
                                                        animation: 'bounce 1.4s ease-in-out 0.2s infinite'
                                                    }}
                                                />
                                                <div 
                                                    style={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#1890ff',
                                                        animation: 'bounce 1.4s ease-in-out 0.4s infinite'
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </Space>
                                </List.Item>
                            )}
                        />
                    </Card>
                    
                    <Text type="secondary" style={{ fontSize: isMobile ? 13 : 14 }}>
                        Proszę czekać, trwa generowanie i podpisywanie dokumentów...
                    </Text>
                </div>
            )}

            {isCompleted && (
                <div style={{ textAlign: 'center' }}>
                    <div 
                        style={{
                            transform: showSuccess ? 'scale(1)' : 'scale(0)',
                            transition: 'transform 0.5s ease-out',
                            marginBottom: isMobile ? 20 : 30
                        }}
                    >
                        <CheckCircleOutlined 
                            style={{ 
                                fontSize: isMobile ? 60 : 80, 
                                color: '#52c41a' 
                            }} 
                        />
                    </div>
                    
                    <Title level={isMobile ? 4 : 3} style={{ marginBottom: isMobile ? 16 : 24 }}>
                        Wszystkie dokumenty zostały wygenerowane!
                    </Title>
                    
                    <Card 
                        style={{ 
                            marginTop: isMobile ? 16 : 20, 
                            marginBottom: isMobile ? 16 : 20,
                            backgroundColor: '#f6ffed',
                            borderColor: '#b7eb8f'
                        }}
                        bodyStyle={{ padding: isMobile ? '12px' : '16px' }}
                    >
                        <Text strong style={{ color: '#52c41a', fontSize: isMobile ? 14 : 16 }}>
                            ✅ Dokumenty zostały pomyślnie wygenerowane
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: isMobile ? 13 : 14 }}>
                            Możesz teraz przystąpić do podpisywania
                        </Text>
                    </Card>
                    
                    <List
                        size="small"
                        dataSource={documents}
                        style={{ textAlign: 'left' }}
                        renderItem={(doc) => (
                            <List.Item style={{ padding: isMobile ? '6px 0' : '8px 0' }}>
                                <Space>
                                    <CheckOutlined style={{ color: '#52c41a' }} />
                                    <Text style={{ fontSize: isMobile ? 13 : 14 }}>{doc}</Text>
                                </Space>
                            </List.Item>
                        )}
                    />
                </div>
            )}
            
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes bounce {
                    0%, 80%, 100% { transform: scale(0); opacity: 0.5; } 
                    40% { transform: scale(1); opacity: 1; }
                }
            `}</style>
        </div>
    );

    // Кастомный модал для мобильных устройств с отступами
    if (isMobile) {
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px'
                }}
            >
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: 'calc(100vw - 20px)',
                        maxHeight: 'calc(100vh - 20px)',
                        backgroundColor: 'white',
                        borderRadius: 8,
                        overflow: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                >
                    {modalContent}
                </div>
            </div>
        );
    }

    // Для десктопа используем обычный Ant Design Modal
    return (
        <Modal
            open={isGenerating || isCompleted}
            footer={null}
            closable={false}
            maskClosable={false}
            width={500}
            centered
            className="document-generation-modal"
            styles={{
                body: {
                    padding: '24px',
                    maxHeight: '90vh',
                    overflowY: 'auto'
                },
                content: {
                    borderRadius: 8
                },
                mask: {
                    backgroundColor: 'rgba(0, 0, 0, 0.85)'
                }
            }}
        >
            {modalContent}
        </Modal>
    );
};

export default DocumentGenerationAnimation